/**
 * A record's position in its DECLARED lifecycle (#2267 port 7).
 *
 * ── what this replaces, and why it was not a process ────────────────────────
 * The legacy CRM record page drew a `StageRail` whenever the resolved form
 * happened to contain a field named `stage` carrying `field_meta.options`. A
 * picklist is not a process: `field_meta.options` is the presentation of a
 * column's vocabulary, it declares no ORDER of passage, no legal moves and no
 * terminal state, and `core.assert_transition` — the thing that actually
 * decides whether a record may move — has never read it. So the rail claimed a
 * flow existed because somebody had named a column, and the same page showed no
 * rail at all for the forty-odd entities that DO declare a real machine.
 *
 * `core.status_machine` (core 0046) is the declaration: states with their own
 * labels, tones and `sort`, and the legal edges between them.
 * `useEntityStatusMachine` resolves it per entity and carries `status_column` —
 * WHICH column the lifecycle governs — so nothing here guesses a field name.
 *
 * ── every declared state is on the rail, in the machine's own order ─────────
 * `category` is an OPEN vocabulary — `open`, `done`, `closed`, `refused`,
 * `live`, `active`, `draft`, `clear`, `blocked`, `admitted` at the time of
 * writing, with no CHECK constraint and no registry of what each means to a
 * rail. Branching on it to push a "refused" state off the ladder would be
 * deriving a JUDGEMENT (rule 42's inverse): a confident wrong answer on the
 * first machine whose vocabulary we did not anticipate.
 *
 * So the rail shows what the machine declares, ordered by the `sort` the module
 * chose. Where a state should sit on a ladder is presentation the machine does
 * not yet carry, and that is a registry gap to file rather than a heuristic to
 * invent here.
 */
import type { ProcessStage } from '@solum/ui'
import type { StatusMachine, StatusState } from '../data/queries'

export interface RecordProcessRail {
  stages: ProcessStage[]
  /** The record's own value for the governed column, whatever it is. */
  current: string
  /** 1-based position of `current` among the declared states, or null when the
   *  record's value is not one the machine declares. */
  position: number | null
  /** The label of the state the record is in — its own code when undeclared. */
  currentLabel: string
  total: number
}

const byDeclaredOrder = (a: StatusState, b: StatusState) =>
  Number(a.sort ?? 0) - Number(b.sort ?? 0) || String(a.code).localeCompare(String(b.code))

/**
 * The rail for one record, or `null` when this entity declares no machine.
 *
 * `null` is the common answer and it is the right one: an entity with no
 * `core.status_machine` row has no process, and drawing an empty rail over it
 * is the defect the legacy field-name heuristic produced.
 */
export function recordProcessRail(
  machine: StatusMachine | null | undefined, row: Record<string, unknown> | undefined,
): RecordProcessRail | null {
  const states = (machine?.states ?? []).slice().sort(byDeclaredOrder)
  if (!machine || states.length === 0) return null

  const column = machine.statusColumn ?? 'status'
  const raw = row?.[column]
  const current = raw == null ? '' : String(raw)
  const index = states.findIndex((state) => String(state.code) === current)

  return {
    stages: states.map((state, i) => ({
      key: String(state.code),
      label: String(state.label ?? state.code),
      /* `upcoming` for EVERY stage while the record's value is undeclared. It is
         the one honest reading: the machine cannot say where an undeclared value
         sits, so nothing is marked reached. `positionText` says so in words. */
      state: index < 0 ? 'upcoming' : i < index ? 'done' : i === index ? 'active' : 'upcoming',
      marker: i + 1,
    })),
    current,
    position: index < 0 ? null : index + 1,
    currentLabel: index < 0 ? current : String(states[index]!.label ?? states[index]!.code),
    total: states.length,
  }
}

/**
 * The bar's accessible name and its compact position line.
 *
 * Separate from the stages because `ProcessBar` requires a REAL name for a
 * focusable scrollable region (WCAG 2.1.1) — "Process" would satisfy the API
 * and tell a screen-reader user nothing about where the record stands.
 */
export function recordProcessLabels(machine: StatusMachine, rail: RecordProcessRail) {
  const flow = String(machine.label || machine.machine)
  return rail.position == null
    ? {
        label: `${flow} — this record's state is not one the lifecycle declares`,
        positionText: rail.current === ''
          ? `${flow} — no state recorded`
          : `${flow} — “${rail.current}” is not a declared state`,
      }
    : {
        label: `${flow} — step ${rail.position} of ${rail.total}: ${rail.currentLabel}`,
        positionText: `Step ${rail.position} of ${rail.total} — ${rail.currentLabel}`,
      }
}
